import { animate, useReducedMotion } from 'framer-motion';
import { RefCallback, useCallback, useEffect, useRef, useState } from 'react';

interface ScrollSpyOptions {
    /** The scrolling container; null while it has not mounted. */
    root: HTMLElement | null;
    /** Distance from the container's top at which a section becomes active. */
    offset?: number;
}

/**
 * Tracks ordered sections with IntersectionObserver, including short final sections.
 * Attach sectionRef(id) to each section and endRef to a marker at the content's end.
 * Match the sections' CSS scroll-margin-top to offset for click-to-scroll navigation.
 */
export const useScrollSpy = (sectionIds: readonly string[], { root, offset = 16 }: ScrollSpyOptions) => {
    const [activeSection, setActiveSection] = useState(sectionIds[0]);
    const sections = useRef(new Map<string, HTMLElement>());
    const callbacks = useRef(new Map<string, RefCallback<HTMLElement>>());
    const observerRef = useRef<IntersectionObserver | null>(null);
    const endRef = useRef<HTMLDivElement | null>(null);
    const animationRef = useRef<{ stop: () => void } | null>(null);
    const updateSelectionRef = useRef<(() => void) | null>(null);
    const reducedMotion = useReducedMotion();
    const stopScrolling = useCallback(() => {
        animationRef.current?.stop();
        animationRef.current = null;
    }, []);

    useEffect(() => {
        if (!root) return;
        const onKeyDown = (event: KeyboardEvent) => {
            if (['ArrowUp', 'ArrowDown', 'PageUp', 'PageDown', 'Home', 'End', ' '].includes(event.key)) {
                stopScrolling();
            }
        };
        root.addEventListener('wheel', stopScrolling, { passive: true });
        root.addEventListener('touchstart', stopScrolling, { passive: true });
        root.addEventListener('pointerdown', stopScrolling, { passive: true });
        root.addEventListener('keydown', onKeyDown);
        return () => {
            stopScrolling();
            root.removeEventListener('wheel', stopScrolling);
            root.removeEventListener('touchstart', stopScrolling);
            root.removeEventListener('pointerdown', stopScrolling);
            root.removeEventListener('keydown', onKeyDown);
        };
    }, [root, stopScrolling]);

    // Stable callback refs also re-observe sections replaced by collapsed/expanded content.
    const sectionRef = useCallback((id: string) => {
        if (!callbacks.current.has(id)) {
            callbacks.current.set(id, (element) => {
                const previous = sections.current.get(id);
                if (previous) observerRef.current?.unobserve(previous);
                if (element) {
                    sections.current.set(id, element);
                    observerRef.current?.observe(element);
                } else {
                    sections.current.delete(id);
                }
            });
        }
        return callbacks.current.get(id)!;
    }, []);

    useEffect(() => {
        const end = endRef.current;
        if (!root || !end || sectionIds.length === 0) return;

        let atEnd = false;
        const updateSelection = () => {
            if (root.clientHeight === 0 || animationRef.current) return;
            const activationLine = root.getBoundingClientRect().top + offset + 1;
            let current = sectionIds[0];
            for (const id of sectionIds) {
                if ((sections.current.get(id)?.getBoundingClientRect().top ?? Infinity) <= activationLine) {
                    current = id;
                }
            }
            if (atEnd && root.scrollHeight > root.clientHeight) current = sectionIds[sectionIds.length - 1];
            setActiveSection(current);
        };
        updateSelectionRef.current = updateSelection;
        const observeSections = () => {
            observerRef.current?.disconnect();
            observerRef.current = new IntersectionObserver(updateSelection, {
                root,
                // A one-pixel activation band just below the configured offset.
                rootMargin: `-${offset}px 0px -${Math.max(0, root.clientHeight - offset - 1)}px 0px`,
            });
            for (const id of sectionIds) {
                const section = sections.current.get(id);
                if (section) observerRef.current.observe(section);
            }
            updateSelection();
        };
        const endObserver = new IntersectionObserver(
            ([entry]) => {
                atEnd = entry.isIntersecting;
                updateSelection();
            },
            { root, threshold: 1 },
        );
        endObserver.observe(end);
        observeSections();
        const resizeObserver = new ResizeObserver(observeSections);
        resizeObserver.observe(root);

        return () => {
            observerRef.current?.disconnect();
            observerRef.current = null;
            updateSelectionRef.current = null;
            endObserver.disconnect();
            resizeObserver.disconnect();
        };
    }, [offset, root, sectionIds]);

    const scrollToSection = useCallback(
        (id: string, behavior: 'smooth' | 'instant' = 'smooth') => {
            const section = sections.current.get(id);
            if (!root || !section) return;
            stopScrolling();
            const position =
                root.scrollTop +
                section.getBoundingClientRect().top -
                root.getBoundingClientRect().top -
                root.clientTop -
                offset;
            const target = Math.max(0, Math.min(position, root.scrollHeight - root.clientHeight));
            if (behavior === 'instant' || reducedMotion || target === root.scrollTop) {
                root.scrollTop = target;
                setActiveSection(id);
                return;
            }
            animationRef.current = animate(root.scrollTop, target, {
                duration: 0.2,
                ease: 'easeOut',
                onUpdate: (value) => {
                    root.scrollTop = value;
                },
                onComplete: () => {
                    animationRef.current = null;
                    updateSelectionRef.current?.();
                },
            });
        },
        [offset, reducedMotion, root, stopScrolling],
    );

    return { activeSection, sectionRef, endRef, scrollToSection };
};
